import React, { useState, useEffect } from 'react';
import { X, Upload, FileText, Trash2, Eye, Lock, Globe, Share2, Loader2, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import FilePreviewModal from './FilePreviewModal';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { swalTheme, swalDangerColor } from '../lib/swalTheme';

const AttachmentsModal = ({ isOpen, onClose, ownertable, ownerid }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  // 🎯 مفيش حاجة في الكود بتحط مفتاح 'username' في localStorage خالص - كان
  // دايماً null، فزرار الحذف (اللي بيتفعّل بس لو المالك = اليوزر الحالي)
  // كان مبيظهرش لأي حد. اسم اليوزر الحقيقي متخزن جوه كائن 'user'
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}').username;

  const fetchDocs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/attachments/${ownertable}/${ownerid}?current_user=${currentUser}`);
      setDocs(res.data);
    } catch (err) { toast.error(t('attachmentsModal.loadError')); } // 🎯
    setLoading(false);
  };

  useEffect(() => { if (isOpen) fetchDocs(); }, [isOpen, ownerid]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('ownertable', ownertable);
    formData.append('ownerid', ownerid);
    formData.append('createby', currentUser);

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/attachments/upload', formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('attachmentsModal.uploadSuccess')); // 🎯
      fetchDocs();
    } catch (err) { toast.error(t('attachmentsModal.uploadFailed')); } // 🎯
    setUploading(false);
  };

  const handleDelete = async (doc) => {
      const result = await Swal.fire({
          ...swalTheme(),
          title: t('attachmentsModal.deleteConfirmTitle'), // 🎯
          text: t('attachmentsModal.deleteConfirmText', { name: doc.document_name }), // 🎯 ديناميكي
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: swalDangerColor(),
          confirmButtonText: t('attachmentsModal.btnYesDelete'), // 🎯
          cancelButtonText: t('attachmentsModal.btnCancel'), // 🎯
          reverseButtons: true,
          didOpen: () => {
            const container = Swal.getContainer();
            if (container) {
              container.style.zIndex = '7000';
            }
          },
          customClass: {
              popup: 'font-sans text-[11px] uppercase tracking-tighter',
              title: 'font-black',
              confirmButton: 'font-bold text-[10px]',
              cancelButton: 'font-bold text-[10px]'
          }
      });
  
      if (result.isConfirmed) {
          const loadId = toast.loading(t('attachmentsModal.processingDelete')); // 🎯
          try {
              const token = localStorage.getItem('token');
              await axios.delete(`/api/attachments/delete/${doc.id}?current_user=${currentUser}`, {
                  headers: { Authorization: `Bearer ${token}` }
              });
              
              Swal.fire({
                  ...swalTheme(),
                  title: t('attachmentsModal.deletedTitle'), // 🎯
                  text: t('attachmentsModal.deletedText'), // 🎯
                  icon: 'success',
                  timer: 2000,
                  showConfirmButton: false,
                  customClass: { popup: 'font-sans text-[11px] uppercase' }
              });
  
              toast.dismiss(loadId);
              fetchDocs(); 
          } catch (err) {
              toast.error(err.response?.data?.detail || t('attachmentsModal.deleteFailed'), { id: loadId }); // 🎯
          }
      }
  };
  
  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 z-[5000] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm animate-in fade-in duration-200 uppercase ${isRTL ? 'font-cairo dir-rtl' : 'font-sans'}`}>
      <div className="bg-surface-card w-[850px] h-[600px] shadow-2xl rounded-sm overflow-hidden flex flex-col border border-line animate-in zoom-in-95">

        {/* Header */}
        <div className="bg-primary-dark text-ink-inverse p-4 flex justify-between items-center shadow-md">
          <div className="flex items-center gap-3">
            <FileText size={20} className="text-ink-inverse/70" />
            <div className="flex flex-col">
                <span className="text-[1.0em] font-black tracking-widest uppercase italic">{t('attachmentsModal.headerTitle')}</span>
                <span className="text-[0.9em] text-ink-inverse/70 font-bold tracking-tighter">{t('attachmentsModal.linkingTo')} {ownertable} / {ownerid}</span>
            </div>
          </div>
          <button onClick={onClose} className="hover:bg-status-danger p-1 rounded-full transition-colors text-ink-inverse"><X size={20}/></button>
        </div>

        {/* Toolbar */}
        <div className="p-4 bg-surface-panel border-b border-line flex justify-between items-center px-8">
            <span className="text-[1.0em] font-black text-primary tracking-widest">{t('attachmentsModal.filesFound')} {docs.length}</span>

            {/* 🎯 الغلاف الخارجي للزر عشان الـ label يشتغل صح */}
            <div className="relative">
              <input type="file" id="file-upload" className="hidden" onChange={handleFileUpload} disabled={uploading} />
              <label htmlFor="file-upload" className={`bg-primary hover:bg-primary-hover text-ink-inverse px-6 py-2 rounded-sm font-black text-[1.0em] flex items-center gap-2 cursor-pointer shadow-lg transition-all active:scale-95 ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                  {uploading ? <Loader2 size={14} className="animate-spin"/> : <Upload size={14} />}
                  {t('attachmentsModal.uploadBtn')}
              </label>
            </div>
        </div>

        {/* Content Table */}
        <div className="flex-1 overflow-auto bg-surface-card">
          <table className="w-full text-left border-collapse">
            <thead className="bg-surface-panel border-b border-line text-ink-secondary font-black text-[1.0em] sticky top-0 uppercase tracking-tighter">
              <tr>
                <th className="p-4 border-r border-line w-1/2">{t('attachmentsModal.thName')}</th>
                <th className="p-4 border-r border-line">{t('attachmentsModal.thOwner')}</th>
                <th className="p-4 border-r border-line text-center">{t('attachmentsModal.thStatus')}</th>
                <th className="p-4 text-center">{t('attachmentsModal.thActions')}</th>
              </tr>
            </thead>
            <tbody className="text-[1.0em] font-bold">
              {docs.map((doc) => (
                <tr key={doc.id} className="border-b border-line-subtle hover:bg-primary-soft transition-colors group">
                  <td className="p-4 border-r border-line text-primary font-black uppercase flex items-center gap-2">
                     <FileText size={14} className="text-ink-secondary group-hover:text-primary"/> {doc.document_name}
                  </td>
                  <td className="p-4 border-r border-line text-ink-secondary uppercase">{doc.createby}</td>
                  <td className="p-4 border-r border-line text-center">
                     {doc.is_private ?
                        <span className="bg-status-danger-bg text-status-danger px-2 py-1 rounded text-[8px] flex items-center justify-center gap-1"><Lock size={10}/> {t('attachmentsModal.statusPrivate')}</span> :
                        <span className="bg-status-success-bg text-status-success px-2 py-1 rounded text-[8px] flex items-center justify-center gap-1"><Globe size={10}/> {t('attachmentsModal.statusPublic')}</span>
                     }
                  </td>
                  <td className="p-4 text-center">
                    <div className="flex justify-center gap-4">
                      <button onClick={() => setPreviewFile(doc)} className="text-primary hover:scale-125 transition-transform"><Eye size={16}/></button>
                      <button className="text-accent hover:scale-125 transition-transform"><Share2 size={16}/></button>
                      {doc.createby === currentUser && (
                        <button className="text-status-danger/40 hover:text-status-danger transition-colors" onClick={() => handleDelete(doc)}><Trash2 size={16}/></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {docs.length === 0 && !loading && (
             <div className="h-full flex flex-col items-center justify-center text-ink-secondary py-20 italic">
                <ShieldCheck size={48} strokeWidth={1} className="mb-4 opacity-20" />
                <span className="uppercase font-black tracking-widest">{t('attachmentsModal.noDocs')}</span>
             </div>
          )}
        </div>

        {/* Preview Sub-Modal */}
        <FilePreviewModal 
            isOpen={!!previewFile} 
            file={previewFile} 
            onClose={() => setPreviewFile(null)} 
        />
      </div>
    </div>
  );
};

export default AttachmentsModal;